/**
 * React Query hooks for issue actions data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listIssueActions,
  executeAction,
  getLatestAction,
  type ExecuteActionData,
} from "@/services/issueActionsService";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch a paginated list of actions for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param page - The page number (default: 1)
 */
export function useIssueActions(issueId: number, page: number = 1) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["issue-actions", issueId, page],
    queryFn: () => listIssueActions(issueId, page),
    staleTime: 30000, // 30 seconds
    enabled: issueId > 0 && isApiReady,
  });
}

/**
 * Hook to fetch the latest completed action of a given type for an issue.
 * Returns 404 (error) when no completed action exists, so retry is disabled.
 *
 * @param issueId - The numeric ID of the issue
 * @param actionType - The action type to look up (e.g. "generate_incident_report")
 * @param enabled - Whether the query should be enabled (default: true)
 */
export function useLatestActionResult(
  issueId: number,
  actionType: string,
  enabled = true
) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["latest-action", issueId, actionType],
    queryFn: () => getLatestAction(issueId, actionType),
    enabled: enabled && issueId > 0 && isApiReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // 404 means no result, don't retry
  });
}

/**
 * Hook to execute an action on a security issue.
 * Invalidates the actions list, activities list, and latest-action cache on settled.
 *
 * EC35: All actions are currently synchronous. When async/ARQ-dispatched
 *       actions are added in the future, polling logic can be added here:
 *       - After mutationFn resolves with status "PENDING", start polling
 *         GET /{issueId}/actions/{actionId} every 2-3 seconds
 *       - Stop polling when status transitions to COMPLETED or FAILED
 *       - Use useQuery with refetchInterval for the polling mechanism
 */
export function useExecuteAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      issueId,
      data,
      force = false,
    }: {
      issueId: number;
      data: ExecuteActionData;
      force?: boolean;
    }) => executeAction(issueId, data, force),
    onSettled: (data, error, variables) => {
      if (error) {
        const message = error instanceof Error ? error.message : "Failed to execute action";
        toast.error(message);
      } else {
        const status = data?.status;
        if (status === "COMPLETED") {
          toast.success("Action executed successfully");
          queryClient.setQueryData(
            ["latest-action", variables.issueId, variables.data.action_type],
            data,
          );
        } else if (status === "FAILED") {
          toast.error("Action execution failed", {
            description: (data?.result as Record<string, unknown>)?.error as string | undefined,
          });
        } else if (status === "NOT_AVAILABLE") {
          toast.info("Action not yet available", {
            description: (data?.result as Record<string, unknown>)?.message as string | undefined,
          });
        } else {
          toast.success("Action submitted");
        }
      }
      queryClient.invalidateQueries({ queryKey: ["issue-actions", variables.issueId] });
      queryClient.invalidateQueries({ queryKey: ["issue-activities"] });
      queryClient.resetQueries({
        queryKey: ["latest-action", variables.issueId, variables.data.action_type],
      });
    },
  });
}
